import { addTableColumns, updateTableColumn } from '@/store/thunks/UpdateTable';
import {
  mkAddColumnCancelBtnTrackAttrs,
  mkAddColumnMarkMetaCheckTrackAttrs,
  mkAddColumnMetaTooltipTrackAttrs,
  mkAddColumnSubmitBtnTrackAttrs,
  mkEditColumnCancelBtnTrackAttrs,
  mkEditColumnMarkMetaCheckTrackAttrs,
  mkEditColumnMetaTooltipTrackAttrs,
  mkEditColumnSubmitBtnTrackAttrs,
} from '@/analytics/props/litTable';
import { TableColumn } from '@/models/Table';
import { ThreadId } from '@/models/Thread';
import { useAppDispatch } from '@/store/hooks';

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  styled,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChangeEvent, Fragment, MouseEvent, ReactNode, useState } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

type ColumnModalProps = {
  column?: TableColumn;
  threadId: ThreadId | undefined;
  onClose: () => void;
};

export const ColumnModal = ({
  column,
  threadId,
  onClose,
}: ColumnModalProps): ReactNode => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState<string>(column?.name || '');
  const [description, setDescription] = useState<string>(
    column?.description || '',
  );
  const [isMetadata, setIsMetadata] = useState<boolean>(
    column?.isMetadata || false,
  );

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value);
  };

  const handleMetadataChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setIsMetadata(event.target.checked);
  };

  const handleSubmit = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (!threadId) {
      return;
    }

    if (!column) {
      dispatch(
        addTableColumns({
          currentThreadId: threadId,
          columns: [{ name, description, isMetadata }],
        }),
      );
      onClose();
      return;
    }

    dispatch(
      updateTableColumn({
        currentThreadId: threadId,
        column: { ...column, name, description, isMetadata },
      }),
    );
    onClose();
  };

  return (
    <Dialog onClose={onClose} open maxWidth="sm" fullWidth>
      <Title>
        {column ? (
          <Fragment>
            <EditOutlinedIcon />
            Edit Column
          </Fragment>
        ) : (
          <Fragment>
            <AddCircleOutlineIcon />
            Add Column
          </Fragment>
        )}
      </Title>
      <Content>
        <form>
          <Label variant="subtitle1">Name</Label>
          <TextBox value={name} onChange={handleNameChange} fullWidth />

          <Label variant="subtitle1" sx={{ mt: 3 }}>
            Definition
          </Label>
          <TextBox
            multiline
            rows={3}
            value={description}
            onChange={handleDescriptionChange}
            fullWidth
          />
          <MetadataCheckboxLabel
            control={
              <MetadataCheckbox
                checked={isMetadata}
                onChange={handleMetadataChange}
                {...(column
                  ? mkEditColumnMarkMetaCheckTrackAttrs()
                  : mkAddColumnMarkMetaCheckTrackAttrs())}
              />
            }
            label={
              <Fragment>
                Mark as metadata column
                <Tooltip
                  title="Check this if the column stores metadata (e.g. year, title, etc.)"
                  arrow
                >
                  <IconButton
                    size="small"
                    {...(column
                      ? mkEditColumnMetaTooltipTrackAttrs()
                      : mkAddColumnMetaTooltipTrackAttrs())}
                  >
                    <MetadataTooltipIcon />
                  </IconButton>
                </Tooltip>
              </Fragment>
            }
          />
        </form>
      </Content>
      <ActionButtons>
        <SaveButton
          onClick={handleSubmit}
          type="submit"
          variant="contained"
          size="small"
          {...(column
            ? mkEditColumnSubmitBtnTrackAttrs()
            : mkAddColumnSubmitBtnTrackAttrs())}
        >
          {column ? 'Save Column' : 'Add Column'}
        </SaveButton>
        <Button
          onClick={onClose}
          variant="text"
          size="small"
          {...(column
            ? mkEditColumnCancelBtnTrackAttrs()
            : mkAddColumnCancelBtnTrackAttrs())}
        >
          Cancel
        </Button>
      </ActionButtons>
    </Dialog>
  );
};

const Title = styled(DialogTitle)`
  align-items: center;
  background-color: ${({ theme }) => theme.color['dark-teal-100'].hex};
  color: ${({ theme }) => theme.color['cream-20'].hex};
  display: flex;
  gap: ${({ theme }) => theme.spacing(0.5)};
  padding: ${({ theme }) => theme.spacing(2.25)};
`;

const Content = styled(DialogContent)`
  background-color: ${({ theme }) => theme.color['dark-teal-100'].hex};
  padding: ${({ theme }) => theme.spacing(2.25)};
`;

const Label = styled(Typography)`
  color: ${({ theme }) => theme.palette.primary.main};
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
`;

const TextBox = styled(TextField)`
  background-color: #14333c;
  border-radius: 4px;

  & .MuiInputBase-root {
    padding: 0;
  }

  input,
  textarea {
    color: ${({ theme }) => theme.color['cream-20'].hex};
    padding: ${({ theme }) => theme.spacing(1) + ' ' + theme.spacing(1.5)};
  }
`;

const ActionButtons = styled(DialogActions)`
  background-color: #14333c;
  padding: ${({ theme }) => theme.spacing(1.5)};
`;

const SaveButton = styled(Button)`
  background-color: ${({ theme }) => theme.color['green-100'].hex};

  &:hover {
    background-color: ${({ theme }) => theme.color['green-80'].hex};
  }
`;

const MetadataCheckboxLabel = styled(FormControlLabel)`
  color: ${({ theme }) => theme.color['cream-20'].hex};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const MetadataCheckbox = styled(Checkbox)`
  color: ${({ theme }) => theme.color['pink-100'].hex};
  font-size: ${({ theme }) => theme.typography.body1.fontSize};
`;

const MetadataTooltipIcon = styled(HelpOutlineIcon)`
  color: ${({ theme }) => theme.color['pink-100'].hex};
`;
